import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment-timezone';
import {
  CalendarCheck,
  CalendarClock,
  CalendarX2,
  Ban,
  Flag,
  Flame,
  Handshake,
  Inbox,
  Info,
  Loader2,
  Mail,
  MessageSquare,
  Moon,
  Phone,
  PhoneIncoming,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { useFetchLeadJourney } from '../../../../services/onboardingBoard';
import { VA_GOV_LABEL } from '../VaGovCheck';
import useAnchoredPopover from '../useAnchoredPopover';

// What happened (Nic 2026-09-24): every milestone and every call, text and
// email for the lead, newest first, with who did it. Times show in the
// viewer's clock.
//
// `compact` (Nic 2026-10-08, the Workspace rail): a few words and a short
// date per line, no card around it; the full line, who, the exact time and
// any note sit behind each row's info button.

const STAGE_WORDS = {
  // Plain words (Nic 2026-09-29).
  in_conversation: 'Talked',
  re_engaged: 'Re-engaged',
  meeting_link_sent: 'Booking link sent',
  meeting_booked: 'Meeting set',
  missed_meeting: 'Missed meeting',
  signup_link_sent: 'Signup link sent',
  account_made: 'Account made',
  agreement_out: 'Agreement sent',
};

// A host's first name ("Josue Bermudez" -> "Josue").
const firstOf = (name) => String(name || '').trim().split(/\s+/)[0] || '';

// One history line: icon, words (text = the full line, short = the compact
// one), tone.
const describe = (item) => {
  const who = item.actor ? ` · ${item.actor}` : '';
  if (item.kind === 'touch') {
    const inbound = item.direction === 'inbound';
    const base =
      item.type === 'call'
        ? inbound
          ? item.contactMade
            ? 'They called in'
            : 'They called (missed)'
          : 'Called them'
        : item.type === 'text'
          ? inbound
            ? 'They texted'
            : 'Texted them'
          : inbound
            ? 'They emailed'
            : 'Emailed them';
    const short =
      item.type === 'call'
        ? inbound
          ? item.contactMade
            ? 'They called'
            : 'Missed their call'
          : item.contactMade
            ? 'Called · reached'
            : 'Called'
        : item.type === 'text'
          ? inbound
            ? 'They texted'
            : 'Texted'
          : inbound
            ? 'They emailed'
            : 'Emailed';
    return {
      icon:
        item.type === 'call'
          ? inbound
            ? PhoneIncoming
            : Phone
          : item.type === 'text'
            ? MessageSquare
            : Mail,
      text: `${base}${inbound ? '' : who}`,
      short,
      detail: item.notes,
      tone: inbound ? 'good' : 'plain',
    };
  }
  const m = item.meta || {};
  switch (item.type) {
    case 'received':
      return { icon: Inbox, text: 'Lead arrived', short: 'Lead arrived', tone: 'plain' };
    case 'cadence_contact_made':
      return { icon: Handshake, text: `Contact made${who}`, short: 'Contact made', tone: 'good' };
    case 'person_added':
      // Add a person (2026-10-20): a rep added them by hand while talking
      // to them, or took them straight to Signing Up.
      return m.took
        ? { icon: Handshake, text: `Taken straight to Signing Up${who}`, short: 'Taken to Signing Up', tone: 'plain' }
        : { icon: Handshake, text: `Added by hand${who}`, short: 'Added by hand', tone: 'plain' };
    case 'pipeline_stage_changed':
      // Plain words (Nic 2026-09-29): the cutover says where they came from;
      // any other move names the Signing Up spot.
      return m.via === 'handoffs_cutover'
        ? {
            icon: Flag,
            text: 'Moved to Signing Up at launch',
            short: 'To Signing Up',
            tone: 'plain',
          }
        : {
            icon: Flag,
            text: `Signing Up: ${STAGE_WORDS[m.to] || m.to}${who}`,
            short: STAGE_WORDS[m.to] || m.to,
            tone: 'plain',
          };
    case 'pipeline_stage_restarted':
      return {
        icon: Flag,
        text: `${STAGE_WORDS[m.stage] || 'Stage'} steps restarted after the meeting`,
        short: 'Steps restarted',
        tone: 'plain',
      };
    case 'invited':
      return {
        icon: MessageSquare,
        text: `Signup link sent${who}`,
        short: 'Signup link sent',
        tone: 'plain',
      };
    case 'joined':
      return {
        icon: UserCheck,
        text: 'Created their account',
        short: 'Made their account',
        tone: 'good',
      };
    case 'calendly_booking_detected':
      return {
        icon: CalendarClock,
        text: `Booked a meeting${firstOf(m.hostName) ? ` with ${firstOf(m.hostName)}` : ''}${
          m.startTime ? ` for ${moment(m.startTime).local().format('ddd, MMM D h:mm A')}` : ''
        }`,
        short: 'Meeting booked',
        tone: 'good',
      };
    case 'calendly_booking_canceled':
      return { icon: CalendarX2, text: 'Meeting canceled', short: 'Meeting canceled', tone: 'bad' };
    case 'calendly_no_show_marked':
      return m.removed
        ? {
            icon: CalendarCheck,
            text: 'No-show mark removed',
            short: 'No-show removed',
            tone: 'plain',
          }
        : {
            icon: CalendarX2,
            text: `Missed the meeting${firstOf(m.hostName) ? ` with ${firstOf(m.hostName)}` : ''}`,
            short: 'Missed the meeting',
            tone: 'bad',
          };
    case 'meeting_outcome_recorded':
      // Never "held" (Nic 2026-09-29): a meeting nobody flagged was met.
      return m.outcome === 'held' || m.outcome === 'met'
        ? {
            icon: CalendarCheck,
            text: m.hostName ? `Met with ${m.hostName.split(/\s+/)[0]}` : `Met${m.auto ? '' : who}`,
            short: 'Met',
            tone: 'good',
          }
        : { icon: CalendarX2, text: `Didn't show${who}`, short: "Didn't show", tone: 'bad' };
    case 'va_gov_confirmed':
      return {
        icon: ShieldCheck,
        text: `VA.gov account: ${VA_GOV_LABEL[m.answer] || m.answer}${who}`,
        short: `VA.gov: ${VA_GOV_LABEL[m.answer] || m.answer}`,
        tone: m.answer === 'yes' ? 'good' : 'plain',
      };
    case 'status_changed':
      if (m.to === 'nurture') {
        const auto = m.via === 'potential_client_expiry' || m.via === 'cadence_expiry';
        return {
          icon: Moon,
          text:
            m.via === 'potential_client_expiry'
              ? m.detail
                ? `Auto nurtured: ${m.detail}`
                : 'Auto nurtured'
              : m.via === 'cadence_expiry'
                ? 'Auto nurtured: the contact steps ran out'
                : `Moved to Nurture${who}`,
          short: auto ? 'Auto nurtured' : 'To Nurture',
          // Why (Nic 2026-10-15): the reason picked, plus any typed words.
          detail: auto ? null : reasonLine(m.reason, m.detail),
          tone: 'bad',
        };
      }
      if (m.to === 'converted') {
        return {
          icon: UserCheck,
          text: 'Became a client account',
          short: 'Client account',
          tone: 'good',
        };
      }
      return null;
    // Nic 2026-10-15: drops, a dropped person reaching out, and a reach-out
    // answered without re-engaging all belong in the history.
    case 'dropped':
      return {
        icon: Ban,
        text: `Dropped${who}`,
        short: 'Dropped',
        detail: reasonLine(m.reason, m.detail),
        tone: 'bad',
      };
    case 'cadence_text_detected':
      return {
        icon: MessageSquare,
        text: 'They texted while dropped',
        short: 'They texted',
        detail: m.preview || null,
        tone: 'good',
      };
    case 'cadence_call_detected':
      return {
        icon: PhoneIncoming,
        text: 'They called while dropped',
        short: 'They called',
        tone: 'good',
      };
    case 'reach_out_left':
      return {
        icon: m.as === 'dropped' ? Ban : Moon,
        text: `${m.as === 'dropped' ? 'Left dropped' : 'Left in Nurture'}${who}`,
        short: m.as === 'dropped' ? 'Left dropped' : 'Left in Nurture',
        detail: m.note || null,
        tone: 'plain',
      };
    // Everything goes in History (Nic 2026-10-23): a re-engage says who did
    // it and who got them; a hand-off says to whom; the Hot Transfer and how
    // it went (from their onboarding record once they signed).
    case 'pulled_in': {
      const by = firstOf(m.byName) || item.actor || '';
      const to = firstOf(m.toName);
      const forSomeoneElse = to && m.toUserId && m.byUserId && m.toUserId !== m.byUserId;
      return {
        icon: Handshake,
        text: `Re-engaged${m.previousStatus === 'dropped' ? ' from Dropped' : ' from Nurture'}${by ? ` by ${by}` : ''}${forSomeoneElse ? `, to ${to}` : ''}`,
        short: forSomeoneElse ? `Re-engaged, to ${to}` : 'Re-engaged',
        tone: 'good',
      };
    }
    case 'reassigned': {
      const to = firstOf(m.toName);
      return {
        icon: UserCheck,
        text: `Reassigned${to ? ` to ${to}` : ''}${who}`,
        short: to ? `To ${to}` : 'Reassigned',
        tone: 'plain',
      };
    }
    // A call picked up in the app and handed to its owner (2026-11-02).
    case 'call_handed_off': {
      const to = firstOf(m.toName);
      return {
        icon: UserCheck,
        text: `Call handed${to ? ` to ${to}` : ' off'}${who}`,
        short: to ? `Call to ${to}` : 'Call handed off',
        tone: 'plain',
      };
    }
    case 'hot_transfer': {
      const to = firstOf(m.toName);
      // Redirected (2026-10-29): who it was meant for, and why not them.
      const instead = m.redirectedFromName
        ? ` (${firstOf(m.redirectedFromName)} ${m.redirect?.reason === 'no_answer' ? "didn't pick up" : 'was not free'})`
        : '';
      return {
        icon: Flame,
        text: `Hot Transfer${to ? ` to ${to}` : ''}${instead}${m.byName ? ` · ${firstOf(m.byName)}` : ''}`,
        short: to ? `Hot Transfer to ${to}` : 'Hot Transfer',
        tone: 'good',
      };
    }
    case 'hot_transfer_result': {
      const to = firstOf(m.toName) || 'their specialist';
      return m.connected
        ? { icon: Flame, text: `Hot Transfer connected with ${to}`, short: 'Transfer connected', tone: 'good' }
        : { icon: Flame, text: `Hot Transfer missed: ${to} calls them back`, short: 'Transfer missed', tone: 'bad' };
    }
    default:
      return null;
  }
};

// "Reason: typed words" for a drop or a nurture.
const reasonLine = (reason, detail) =>
  [reason, detail].filter(Boolean).join(': ') || null;

const TONE = {
  good: 'bg-emerald-50 text-emerald-600 dark:!bg-emerald-500/15 dark:text-emerald-300',
  bad: 'bg-red-50 text-red-500 dark:!bg-red-500/15 dark:text-red-300',
  plain: 'bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-400',
};

// Today shows the time, anything older the date.
const shortWhen = (at) => {
  const t = moment(at).local();
  return t.isSame(moment(), 'day') ? t.format('h:mm A') : t.format('MMM D');
};

// The row's info button: the app's own popover on hover or focus (never the
// browser's tooltip), with the full line, who, the exact time and the note.
const RowInfo = ({ it, d }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const timer = useRef(null);
  const style = useAnchoredPopover(open, btnRef, 260);
  const show = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), 100);
  };
  const hide = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(false), 100);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="More about this"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onClick={() => setOpen((o) => !o)}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-slate-300 outline-none hover:bg-slate-100 hover:text-slate-500 focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-600 dark:hover:!bg-white/10 dark:hover:text-slate-300"
      >
        <Info size={13} />
      </button>
      {open && style
        ? createPortal(
            <div
              role="tooltip"
              onMouseEnter={show}
              onMouseLeave={hide}
              style={{ ...style, overflowY: 'visible', maxHeight: 'none' }}
              className="z-[1085] rounded-xl bg-white p-3 text-left shadow-[0_10px_30px_rgba(15,23,42,0.16),0_2px_6px_rgba(15,23,42,0.08)] ring-1 ring-slate-200 dark:!bg-surface-card dark:ring-white/10"
            >
              <div className="text-[13px] font-semibold leading-snug text-slate-800 dark:text-slate-100">
                {d.text}
              </div>
              <div className="mt-0.5 text-[11px] tabular-nums text-slate-400">
                {moment(it.at).local().format('ddd, MMM D, h:mm A')}
              </div>
              {d.detail ? (
                <p className="mb-0 mt-2 whitespace-pre-wrap border-t border-slate-100 pt-2 text-xs leading-relaxed text-slate-600 dark:border-white/10 dark:text-slate-300">
                  {d.detail}
                </p>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
};

// `bare`: the surrounding section already says "What happened".
const JourneyTimeline = ({ leadId, bare = false, compact = false }) => {
  const q = useFetchLeadJourney(leadId);
  const [all, setAll] = useState(false);
  const history = (q.data?.items || [])
    .map((it) => ({ it, d: describe(it) }))
    .filter((x) => x.d);
  const shown = all ? history : history.slice(0, 8);

  return (
    <div
      className={
        compact
          ? ''
          : 'rounded-xl bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_8px_rgba(15,23,42,0.05)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10'
      }
    >
      {bare ? null : (
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          What happened
        </div>
      )}
      {q.isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 size={16} className="animate-spin text-slate-400" />
        </div>
      ) : !history.length ? (
        <p className="mb-0 text-xs text-slate-400">Nothing yet.</p>
      ) : (
        <>
          {compact ? (
            <ol className="mb-0 list-none space-y-[2px] pl-0">
              {shown.map(({ it, d }) => (
                <li
                  key={`${it.kind}-${it.id}`}
                  className="flex items-center gap-[8px] py-[2px]"
                >
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                      TONE[d.tone],
                    )}
                  >
                    <d.icon size={11} />
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-slate-700 dark:text-slate-200">
                    {d.short || d.text}
                  </span>
                  <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                    {shortWhen(it.at)}
                  </span>
                  <RowInfo it={it} d={d} />
                </li>
              ))}
            </ol>
          ) : (
            <ol className="mb-0 list-none space-y-2 pl-0">
              {shown.map(({ it, d }) => (
                <li
                  key={`${it.kind}-${it.id}`}
                  className="flex items-start gap-2.5"
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                      TONE[d.tone],
                    )}
                  >
                    <d.icon size={12} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm text-slate-700 dark:text-slate-200">
                        {d.text}
                      </span>
                      <span className="shrink-0 text-[11px] tabular-nums text-slate-400">
                        {moment(it.at).local().format('MMM D, h:mm A')}
                      </span>
                    </span>
                    {d.detail ? (
                      <span className="mt-0.5 line-clamp-2 block text-xs text-slate-400">
                        {d.detail}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {history.length > 8 ? (
            <button
              type="button"
              onClick={() => setAll((v) => !v)}
              className="mt-2 text-xs font-semibold text-sky-600 outline-none hover:text-sky-500 focus:outline-none dark:text-sky-400"
            >
              {all ? 'Show less' : `Show all ${history.length}`}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
};

export default JourneyTimeline;
